/**
 * One outline row synthesised from a loaded record, so a Detail surface can drive the very same bulk
 * handlers the checkbox selection drives.
 *
 * The board's ⌘K and the quickview's ⌘K must offer one record the same actions. The only way that
 * stays true as handlers change is for both to run the same code: the Detail commands build the row
 * the board would have handed over and call the bulk handler with a selection of one.
 *
 * Everything entity-specific arrives as an argument. The live Task names its owning branch
 * `serviceLine` and derives subtask-ness by comparing `parent` to it; the template Task names it
 * `templateLine` and carries a server-assigned `isSubtask`. Both differences are read through the
 * caller's {@link OutlineRecordAccess}, so this file never learns either entity.
 */
import { Entity } from '@jms/model';
import { BulkCommandArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';

/** Only what the row synthesis reads — a Detail model is a superset of this. */
export interface OutlineRecordTask {
  _id?: string;
  name?: string;
  parent?: Entity;
}

/** Only what the row synthesis reads. A line's outline row is its own parent-less root. */
export interface OutlineRecordLine {
  _id?: string;
  name?: string;
}

/** How a Detail record answers the two questions the synthesis cannot derive from the row shape alone. */
export interface OutlineRecordAccess<TTask extends OutlineRecordTask> {
  /** The record's owning line — `serviceLine` live, `templateLine` on the template side. */
  lineRef: (record: TTask) => Entity | undefined;
  /** Whether the task hangs off another task. Derived live; a stored flag on a template task. */
  isSubtask: (record: TTask) => boolean;
}

/**
 * A Task's outline row. `parentId` is the prefixed row key of whatever it hangs under — its line for
 * a root task, its parent task for a subtask — which is what `isOutlineRootTask` reads to tell the
 * two apart, so a synthetic row must key it the same way the server does.
 */
export function outlineRowForTask<TTask extends OutlineRecordTask>(record: TTask | null | undefined, kinds: OutlineEntityKinds, access: OutlineRecordAccess<TTask>): OutlineRow | null {

  if (!record?._id) return null;
  const { linePrefix, taskPrefix } = kinds;
  const line = access.lineRef(record);
  const parentId = record.parent?._id;
  return {
    _id: record._id,
    rowId: `${taskPrefix}${record._id}`,
    ...(parentId ? { parentId: access.isSubtask(record) ? `${taskPrefix}${parentId}` : `${linePrefix}${parentId}` } : {}),
    hasChildren: false,
    sourceEntity: kinds.task,
    sourceId: record._id,
    ...outlineLineField(kinds, line ?? { _id: '' } as Entity),
    name: record.name ?? '',
    sortOrder: '',
  } as OutlineRow;
}

/** A line's outline row. Lines are the tree roots, so they carry no `parentId`. */
export function outlineRowForLine(record: OutlineRecordLine | null | undefined, kinds: OutlineEntityKinds): OutlineRow | null {

  if (!record?._id) return null;
  return {
    _id: record._id,
    rowId: `${kinds.linePrefix}${record._id}`,
    hasChildren: false,
    sourceEntity: kinds.line,
    sourceId: record._id,
    ...outlineLineField(kinds, { _id: record._id, name: record.name } as Entity),
    name: record.name ?? '',
    sortOrder: '',
  } as OutlineRow;
}

/** The bulk-handler args for a selection of exactly one row. */
export function singleOutlineCommandArgs(action: string, row: OutlineRow): BulkCommandArgs<OutlineRow> {

  return { action, data: [row] };
}

/**
 * Writes the owning-line reference under whichever field name this outline's projection uses.
 *
 * `kinds.lineRef` READS that field; there is no writer counterpart, and inventing one on the kinds
 * would put the same knowledge in two places. Instead the probe asks the accessor which of the two
 * known field names it reads, so a synthetic row round-trips through `kinds.lineRef` on both sides.
 */
function outlineLineField(kinds: OutlineEntityKinds, line: Entity): Record<string, Entity> {

  // A sentinel id the caller's value can never collide with, so an empty `line._id` still probes.
  const probe = { _id: '__outline-line-field-probe__' } as Entity;
  for (const field of ['serviceLine', 'templateLine']) {
    if (kinds.lineRef({ [field]: probe } as never)?._id === probe._id) return { [field]: line };
  }
  return { serviceLine: line };
}
